import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationService } from '../services/organization-service';
import httpClient from '../services/http-client';
import { 
  OrganizationSettings,
  UpdateOrganizationSettingsRequest,
  OrganizationSettingsImportRequest,
  OrganizationSettingsImportResult
} from '../types';

// Create an organization service instance using the default HTTP client
const organizationService = new OrganizationService(httpClient);

// Query keys
export const organizationQueryKeys = {
  all: ['organization'] as const,
  settings: () => [...organizationQueryKeys.all, 'settings'] as const,
};

/**
 * Hook to get organization settings
 */
export function useOrganizationSettings() {
  return useQuery({
    queryKey: organizationQueryKeys.settings(),
    queryFn: () => organizationService.getOrganizationSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update organization settings
 */
export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateOrganizationSettingsRequest) =>
      organizationService.updateOrganizationSettings(request),
    onSuccess: (updatedSettings) => {
      // Update the cache with the new settings
      queryClient.setQueryData(organizationQueryKeys.settings(), updatedSettings);
      
      // Optionally invalidate to refetch fresh data
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.settings() });
    },
  });
}

/**
 * Hook to import organization settings from a previously-exported file.
 */
export function useImportOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: OrganizationSettingsImportRequest) =>
      organizationService.importOrganizationSettings(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.settings() });
    },
  });
}

/**
 * Hook to export organization settings as a downloadable JSON file.
 * Triggers a browser file-save dialog on success.
 */
export function useExportOrganizationSettings() {
  return useMutation({
    mutationFn: async () => {
      const blob = await organizationService.exportOrganizationSettings();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organization-settings-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
}
